import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getEffectiveRole, getRoleLabel, isCopimAccount, isCopimRole } from '../lib/copimAccess';
import { Send, MessageCircle, X, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

export const AIChat = () => {
  const { api, user, isCopimMode } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const effectiveRole = getEffectiveRole(user);
  const isCopimAgent = isCopimMode && (isCopimRole(effectiveRole) || isCopimAccount(user));

  const agentCopy = useMemo(() => {
    if (!isCopimAgent) {
      return {
        title: 'Asistente IA',
        subtitle: 'Rovi CRM',
        greeting: '¡Hola! Soy tu asistente de ventas. ¿En qué puedo ayudarte?',
        placeholder: 'Escribe tu mensaje...',
        quickPrompts: [
          '¿Cómo va mi meta de ventas?',
          '¿Qué lead debo contactar primero?',
          'Dame un tip de ventas',
        ],
      };
    }

    const rolePrompts = {
      copim_admin: [
        'Resume los KPIs nacionales de COPIM',
        '¿Qué asociaciones requieren atención?',
        '¿Qué producto del marketplace conviene impulsar?',
      ],
      copim_operator: [
        '¿Qué socios locales necesitan seguimiento?',
        'Resume cobranza, eventos y cursos pendientes',
        'Dame una acción para activar el marketplace local',
      ],
      copim_member: [
        '¿Cómo puedo mejorar mi perfil profesional?',
        '¿Qué cursos o eventos me convienen?',
        'Recomiéndame recursos del marketplace',
      ],
    };

    return {
      title: 'Agente COPIM',
      subtitle: getRoleLabel(effectiveRole),
      greeting: 'Hola, soy tu agente COPIM. Puedo ayudarte con socios, cursos, eventos, marketplace y prioridades institucionales.',
      placeholder: 'Pregunta sobre COPIM...',
      quickPrompts: rolePrompts[effectiveRole] || rolePrompts.copim_admin,
    };
  }, [effectiveRole, isCopimAgent]);

  const loadHistory = useCallback(async () => {
    try {
      const response = await api.get('/chat/history?limit=30');
      setMessages(response.data);
      setHistoryLoaded(true);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  }, [api]);

  useEffect(() => {
    if (isOpen && !historyLoaded) {
      loadHistory();
    }
  }, [historyLoaded, isOpen, loadHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input, id: Date.now() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/chat', { content: input });
      setMessages((prev) => [...prev, { 
        role: 'assistant', 
        content: response.data.content,
        id: response.data.id 
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: 'Lo siento, hubo un error. Por favor intenta de nuevo.',
        id: Date.now()
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-20 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group ${isOpen ? 'hidden' : ''}`}
        data-testid="ai-chat-toggle"
      >
        <div className="absolute inset-0 rounded-full bg-primary animate-pulse-ring opacity-0 group-hover:opacity-100" />
        <MessageCircle className="w-6 h-6 relative z-10" />
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div 
          className="fixed bottom-20 right-6 z-50 w-96 h-[32rem] bg-card rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden animate-slide-in"
          data-testid="ai-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">{agentCopy.title}</h3>
                <p className="text-xs opacity-80">{agentCopy.subtitle}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="text-primary-foreground hover:bg-white/20"
              data-testid="ai-chat-close"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Sparkles className="w-12 h-12 mx-auto text-primary/50 mb-3" />
                <p className="text-sm text-muted-foreground mb-4">
                  {agentCopy.greeting}
                </p>
                <div className="space-y-2">
                  {agentCopy.quickPrompts.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => setInput(prompt)}
                      className="block w-full text-left px-3 py-2 text-xs bg-muted/50 hover:bg-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        <Sparkles className="w-4 h-4" />
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-br-md'
                        : 'bg-muted text-foreground rounded-bl-md'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-3 justify-start">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      <Sparkles className="w-4 h-4" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="bg-muted px-4 py-2.5 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 border-t border-border">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={agentCopy.placeholder}
                className="flex-1 rounded-full bg-muted/50"
                disabled={loading}
                data-testid="ai-chat-input"
              />
              <Button
                type="submit"
                size="icon"
                disabled={!input.trim() || loading}
                className="rounded-full"
                data-testid="ai-chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
